<template lang="pug">
    ._tabc2(:style="{ width, height }")
        ._tabcb.border-bottom(
            v-if="$slots.top"
        )
            slot(name="top")
        ._tabcp(
            ref="main",
            :class="hideOverflow ? '_tabch' : void 0",
            @touchstart="scrollReset",
            @wheel="scrollReset",
            @scroll="scroll"
        )
            slot
        ._tabcb.border-top(
            v-if="$slots.bottom"
        )
            slot(name="bottom")
</template>

<script>
    import $util from '../../global/util';
    import $device from '../../global/device';
    import { minScrollbarHeight } from '../../init';

    const scrollTimeout = 300;    // ms

    /**
     * This is a container for a tab bar and a collection of tab panes. It displays the tab bar above the panes.
     * By default, the panes may be dragged on touch devices.
     */
    export default {
        name: 'ib-tab-container',

        props: {
            /**
             * The key of the tab to display. This can be used via `v-model`, as an alternative to embedding
             * `ib-tab-bar` in a slot.
             */
            value: [String,Number],
            /**
             * Width in any CSS units.
             */
            width: String,
            /**
             * Height in any CSS units.
             */
            height: String
        },

        computed: {
            sp() {
                return $util.scrollProps(this.$rtl);
            },

            hideOverflow() {
                return minScrollbarHeight > 0;
            }
        },

        watch: {
            value: {
                handler: 'selectvalue',
                immediate: true
            }
        },

        beforeCreate() {
            this.isTabContainer = true;
            this.panes = [];
        },

        mounted() {
            this.$nextTick(() => {
                this.select(this.ivalue);
            });
            this.readyto = setTimeout(() => {
                this.readyto = 0;
                this.ready = true;
            }, 300);
        },

        beforeDestroy() {
            this.dragto && clearTimeout(this.dragto);
            this.readyto && clearTimeout(this.readyto);
        },

        methods: {
            sync() {
                if (!this.willParse) {
                    this.willParse = true;
                    this.$nextTick(this.parseNodes);
                }
            },

            parseNodes() {
                this.panes = (this.$slots.default || []).filter(vnode =>
                    vnode.componentOptions?.Ctor.options.name === 'ib-tab-pane');
                this.willParse = false;
            },

            selectvalue(value) {
                if (this.ivalue !== value) {
                    this.ivalue = value;
                    this.select(value);
                }
            },

            select(value) {
                if (value != null) {
                    const index = this.panes.findIndex(pane => pane.key == value);
                    if (index >= 0) {
                        const main = this.$refs.main;
                        main.scrollTo({
                            left: this.sp.base * main.scrollWidth + this.sp.step * index * main.clientWidth,
                            behavior: this.ready ? 'smooth' : void 0
                        });
                        this.scrollSelf = Date.now() + scrollTimeout;
                    }
                }
            },

            scrollReset() {
                this.scrollSelf = 0;
            },

            scroll() {
                let scrollSelf = this.scrollSelf;
                if (scrollSelf) {
                    const now = Date.now();
                    this.scrollSelf = scrollSelf = now > scrollSelf ? 0 : now + scrollTimeout;
                }
                if (!scrollSelf) {
                    const main = this.$refs.main;
                    const index = Math.round((this.sp.base * main.scrollWidth + this.sp.step * main.scrollLeft) / main.clientWidth);
                    const pane = this.panes[index];
                    if (pane) {
                        const value = pane.key;
                        const bar = this.bar;
                        if (bar && value != null) {
                            bar.selectvalue(value, null, true);
                        }
                        if (value !== this.ivalue) {
                            this.$emit('input', this.ivalue = value);
                        }
                    }
                }
            }
        }
    }
</script>

<style lang="less">
    @import "../../less/globals.less";

    ._tabc2 {
        display: flex;
        flex-direction: column;
    }
    ._tabcb {
        flex: 0 0 auto;
    }
    ._tabcp {
        display: flex;
        flex-direction: row;
        flex: 1 1 auto;
        overflow-x: scroll;
        overflow-y: hidden;
        -webkit-scroll-snap-points-x: repeat(100%);
        scroll-snap-points-x: repeat(100%);
        scroll-snap-type: x mandatory;
        .hide-scrollbar();
    }
    ._tabch {
        overflow-x: hidden;
    }
</style>

<ib-example id="basic" name="Basic">
    <template lang="pug">
        ib-tab-container(height="160px")
            template(v-slot:top)
                ib-tab-bar
                    ib-tab(key="starks") Starks
                    ib-tab(key="lannisters") Lannisters
                    ib-tab(key="targaryens") Targaryens
            ib-tab-pane(key="starks")
                article.inset-16
                    ul
                        li Eddard
                        li Catelyn
                        li Robb
                        li Sansa
                        li Brandon
                        li Arya
                        li Rickon
            ib-tab-pane(key="lannisters")
                article.inset-16
                    ul
                        li Tywin
                        li Cersei
                        li Jamie
                        li Tyrion
            ib-tab-pane(key="targaryens")
                article.inset-16
                    ul
                        li Viserys
                        li Daenerys
    </template>
</ib-example>

<ib-example id="grow" name="Width-growing tabs with large icons, one disabled, one with badge">
    <template lang="pug">
        include ../../icon/finance

        ib-tab-container
            template(v-slot:top)
                ib-tab-bar(align="grow")
                    ib-tab.icon-32(key="btc", aria-label="Bitcoin")
                        +icon-finance-bitcoin
                    ib-tab.icon-32(key="chf", aria-label="Swiss Franc", disabled)
                        +icon-finance-chf
                    ib-tab.icon-32(key="eur", aria-label="Euro")
                        ib-badge(:value="3")
                            +icon-finance-euro
                    ib-tab.icon-32(key="rub", aria-label="Ruble")
                        +icon-finance-ruble
                    ib-tab.icon-32(key="jpy", aria-label="Yen")
                        +icon-finance-jpy
            ib-tab-pane.inset-24(key="btc")
                p This is some Bitcoin related content.
            ib-tab-pane.inset-24(key="chf")
                p This is some Swiss Franc related content.
            ib-tab-pane.inset-24(key="eur")
                p This is some Euro related content.
            ib-tab-pane.inset-24(key="rub")
                p This is some Russian Ruble related content.
            ib-tab-pane.inset-24(key="jpy")
                p This is some Japanese Yen related content.
    </template>
</ib-example>
